// src/types/ViewTypes.ts

/**
 * Chart configuration options
 */
export interface ChartConfiguration {
    showLegend: boolean;
    showGrid: boolean;
    showTooltip: boolean;
    animation: boolean;
    aspectRatio: number;
    responsive: boolean;
    colorScheme: string;
    title: string;
    xAxisLabel: string;
    yAxisLabel: string;
    lineStyle: 'linear' | 'monotone' | 'step' | 'natural';
    lineWidth: number;
    pointSize: number;
    barPadding: number;
    showTrendline: boolean;
    fontSize: number;
    backgroundColor: string;
    labelRotation: number;
  }
  
  /**
   * Map view configuration
   */
  export interface MapViewConfiguration {
    colorScale: string;
    showLegend: boolean;
    showBorders: boolean;
    showLabels: boolean;
    showTooltip: boolean;
    tooltipVariables: string[];
    zoomLevel: number;
    center: [number, number];
    basemap: string;
    bordersColor: string;
    bordersWidth: number;
    highlightColor: string;
    nullValueColor: string;
    labelSize: number;
    labelColor: string;
  }
  
  /**
   * View modes for the application
   */
  export enum ViewMode {
    MAP = 'map',
    ANALYSIS = 'analysis',
    COMPARISON = 'comparison',
    ABOUT = 'about'
  }
  
  /**
   * Chart types available in the application
   */
  export enum ChartType {
    LINE = 'line',
    BAR = 'bar',
    SCATTER = 'scatter',
    PIE = 'pie',
    RADAR = 'radar',
    HEATMAP = 'heatmap',
    HISTOGRAM = 'histogram',
    BOX_PLOT = 'boxPlot'
  }
  
  /**
   * Saved view configuration
   */
  export interface SavedView {
    id: string;
    name: string;
    description?: string;
    viewMode: ViewMode;
    filters: {
      years?: number[];
      regions?: string[];
      countries?: string[];
      variables?: string[];
    };
    chartConfig?: Partial<ChartConfiguration>;
    mapConfig?: Partial<MapViewConfiguration>;
    chartType?: ChartType;
    createdAt: string;
    updatedAt: string;
  }
  
  /**
   * User preferences for the application
   */
  export interface UserPreferences {
    theme: 'light' | 'dark' | 'system';
    defaultViewMode: ViewMode;
    defaultChartType: ChartType;
    defaultColorScheme: string;
    defaultMapColorScale: string;
    showWelcomeScreen: boolean;
    zoomOnSelect: boolean;
    animationDuration: number;
    showTooltips: boolean;
    language: string;
    dateFormat: string;
    numberFormat: string;
    rememberFilters: boolean;
  }
  
  /**
   * Export configuration options
   */
  export interface ExportConfiguration {
    format: 'png' | 'jpeg' | 'svg' | 'pdf' | 'csv' | 'xlsx' | 'json';
    fileName: string;
    width: number;
    height: number;
    dpi: number;
    includeFilters: boolean;
    includeLegend: boolean;
    includeTitle: boolean;
    includeFooter: boolean;
  }
  
  /**
   * Share configuration options
   */
  export interface ShareConfiguration {
    includeFilters: boolean;
    includeViewConfiguration: boolean;
    generateShortLink: boolean;
    expiresAfter?: number; // In hours, undefined means never expires
  }